// Quote Builder — picker host.
//
// MOVED INTO THE LEFT PANEL 2026-09-05. It was the right pane's middle
// section, between the quote meta and the item list, which split one task
// across the full screen width: you browsed a range on the left and
// configured it on the right. The right column is now purely the committed
// quote record.
//
// Nothing in this module knew which column it was in, and nothing in any
// picker did either — every one is scoped to the host it is handed and makes
// no global DOM query. The move was a change to WHICH host index.ts passes,
// not to any picker. Keep it that way: a `document.querySelector` in here or
// in a picker re-couples them to a layout.
//
// The active picker is still driven by the Products panel through
// picker-selection.ts, which is now a sibling rather than the other column.

import type { QuoteStore } from "./state";
import {
  subscribePicker,
  getActivePicker,
  setActivePicker,
  notifyPickerRemount,
  getSelectedSjbCode,
  setSelectedSjbCode,
  subscribeSelectedSjb,
  getSjbPanelOpen,
  setSjbPanelOpen,
  subscribeSjbPanel,
  subscribeSelectedCab,
  getSelectedCab,
  type ActivePicker,
} from "./picker-selection";
import {
  subscribeBreadcrumb,
  getBreadcrumb,
  invokeBreadcrumbClear,
  clearBreadcrumb,
  type BreadcrumbState,
} from "./picker-breadcrumb";
import { escapeHtml } from "./formatters";
import { mountDoorPicker, type DoorPickerHandle } from "./door-picker";
import { mountSjbCabinetPicker } from "./sjb-cabinet-picker";
import { mountSjbPanelPicker } from "./sjb-panel-picker";
import {
  mountCabinetPicker,
  renderCabinetPickerEmptyState,
  isDrawerPackCab,
  type CabinetPickerHandle,
} from "./cabinet-picker";
import { mountDrawerPackPicker, type DrawerPackPickerHandle } from "./drawer-pack-picker";
import type { W8Cab } from "./types";
import {
  mountHandlePicker,
  mountStoragePicker,
  mountWorktopPicker,
  mountPanelPicker,
  mountPlinthPicker,
  mountLightingPicker,
  mountAppliancePicker,
  mountSundriesPicker,
  mountLabourPicker,
  type SimplePickerHandle,
} from "./simple-pickers";

type AnyHandle = DoorPickerHandle | CabinetPickerHandle | DrawerPackPickerHandle | SimplePickerHandle;

function renderBreadcrumb(s: BreadcrumbState): string {
  if (!s.crumbs.length) return "";
  const parts: string[] = [`<span class="qb-picker-breadcrumb__label">Configuring</span>`];
  s.crumbs.forEach((c, i) => {
    parts.push(`<span class="qb-picker-breadcrumb__sep">${i === 0 ? "|" : "›"}</span>`);
    if (c.sup) {
      parts.push(`<span class="qb-picker-breadcrumb__sup">${escapeHtml(c.sup)}</span>`);
    }
    if (c.label) {
      parts.push(`<span class="qb-picker-breadcrumb__crumb">${escapeHtml(c.label)}</span>`);
    }
  });
  if (s.canClear) {
    parts.push(`<button type="button" class="qb-picker-breadcrumb__clear" data-role="breadcrumb-clear">Clear</button>`);
  }
  return `<div class="qb-picker-breadcrumb" data-role="picker-breadcrumb">${parts.join(" ")}</div>`;
}

export function mountPickerArea(host: HTMLElement, store: QuoteStore): () => void {
  let current: AnyHandle | null = null;
  let currentCat: ActivePicker = null;
  // W8 cabinet picker tracks selected cab too — re-mounts when the cab
  // changes even if the active picker category hasn't.
  let currentCab: W8Cab | null = null;
  // Tracked alongside currentCab so switching between two SJB cabinets
  // re-points the pane; both are null-for-the-other's-flow by design.
  let currentSjb: string | null = null;

  function repaintBreadcrumb(): void {
    const slot = host.querySelector<HTMLElement>("[data-role='breadcrumb-slot']");
    if (!slot) return;
    slot.innerHTML = renderBreadcrumb(getBreadcrumb());
    const btn = slot.querySelector<HTMLButtonElement>("[data-role='breadcrumb-clear']");
    btn?.addEventListener("click", () => invokeBreadcrumbClear());
  }

  function paint(cat: ActivePicker, force: boolean = false): void {
    const cab = getSelectedCab();
    // For cabinets, also re-mount on cab swap even when category is stable.
    const cabChanged = cat === "cabinets" && cab !== currentCab;
    const sjbChanged = cat === "cabinets" && getSelectedSjbCode() !== currentSjb;
    if (!force && !cabChanged && !sjbChanged && cat === currentCat && current !== null) return;
    if (current) {
      current.unmount();
      current = null;
    }
    currentCat = cat;
    currentCab = cab;
    currentSjb = getSelectedSjbCode();
    clearBreadcrumb();

    if (!cat) {
      host.innerHTML = `
        <div class="qb-picker-area qb-picker-area--empty">
          <div class="qb-picker-area__empty">
            <strong>Nothing selected yet</strong>
            Pick a category or a product above,<br>and it opens here to configure.
          </div>
        </div>
      `;
      return;
    }

    // W8 cabinet picker requires a selected cab; without one, show the
    // empty hint so the panel surface isn't blank. An SJB cabinet is the
    // exception: it is category "cabinets" but carries no W8 cab, so it is
    // identified by its own selection channel and must pass this guard.
    if (cat === "cabinets" && !cab && !getSelectedSjbCode()) {
      host.innerHTML = `
        <div class="qb-picker-area qb-picker-area--empty">
          ${renderCabinetPickerEmptyState()}
        </div>
      `;
      return;
    }

    host.innerHTML = `
      <div class="qb-picker-area">
        <div data-role="breadcrumb-slot"></div>
        <div class="qb-door-picker" data-role="picker-slot"></div>
      </div>
    `;
    const slot = host.querySelector<HTMLElement>("[data-role='picker-slot']")!;

    switch (cat) {
      case "doors":      current = mountDoorPicker(slot, store); break;
      case "cabinets": {
        // An SJB cabinet wins when one is selected: it is a different
        // catalogue with its own pricing model, and it carries no W8 cab.
        // Confirming or cancelling clears the selection, which drops the
        // pane back to whatever the tab state says.
        const sjbCode = getSelectedSjbCode();
        if (sjbCode) {
          current = mountSjbCabinetPicker(slot, store, sjbCode, () => {
            setSelectedSjbCode(null);
            setActivePicker(null);
            notifyPickerRemount();
          });
          break;
        }
        // Drawer-pack cabs route to the dedicated drawer-pack picker so
        // the configurator only surfaces drawer-pack-relevant options
        // (no hinge side, no drawerline, no gable reduction). Separated
        // from the cabinet picker on 2026-05-26.
        current = isDrawerPackCab(cab!)
          ? mountDrawerPackPicker(slot, store, cab!)
          : mountCabinetPicker(slot, store, cab!);
        break;
      }
      case "handles":    current = mountHandlePicker(slot, store); break;
      case "storage":    current = mountStoragePicker(slot, store); break;
      case "worktops":   current = mountWorktopPicker(slot, store); break;
      case "panels": {
        // An SJB panel wins when that flow is open, for the same reason an
        // SJB cabinet does: it is a different catalogue with its own pricing
        // model. Confirming or cancelling clears the flag, dropping the pane
        // back to the generic panel picker.
        if (getSjbPanelOpen()) {
          current = mountSjbPanelPicker(slot, store, () => {
            setSjbPanelOpen(false);
            setActivePicker(null);
            notifyPickerRemount();
          });
          break;
        }
        current = mountPanelPicker(slot, store);
        break;
      }
      case "plinth":     current = mountPlinthPicker(slot, store); break;
      case "lighting":   current = mountLightingPicker(slot, store); break;
      case "appliances": current = mountAppliancePicker(slot, store); break;
      case "sundries":   current = mountSundriesPicker(slot, store); break;
      case "labour":     current = mountLabourPicker(slot, store); break;
    }
    repaintBreadcrumb();
  }

  paint(getActivePicker());
  const unsubPicker = subscribePicker((cat, force) => paint(cat, force));
  const unsubCrumb = subscribeBreadcrumb(() => repaintBreadcrumb());
  const unsubCab = subscribeSelectedCab(() => paint(getActivePicker()));
  // Selecting a different SJB cabinet must re-point the pane even though the
  // ActivePicker value ("cabinets") has not changed.
  const unsubSjb = subscribeSelectedSjb(() => paint(getActivePicker(), true));
  // Opening the SJB panel flow must re-point the pane even though the
  // ActivePicker value ("panels") has not changed.
  const unsubSjbPanel = subscribeSjbPanel(() => paint(getActivePicker(), true));

  return (): void => {
    unsubPicker();
    unsubCrumb();
    unsubCab();
    unsubSjb();
    unsubSjbPanel();
    if (current) { current.unmount(); current = null; }
    host.innerHTML = "";
  };
}
